import React, { useEffect, useState, useMemo } from "react";
import { useLanguage } from "../../LanguageContext";
import translations from "../../translation_hero";

function Hero() {
  const { language } = useLanguage();
  const t = translations[language];
  const [activeSection, setActiveSection] = useState("");
  const [typedText, setTypedText] = useState("");
  const [typingComplete, setTypingComplete] = useState(false);
  const fullText = t.description;

  useEffect(() => {
    const sections = document.querySelectorAll("section");
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.6,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, options);

    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, []);

  // Typing effect
  useEffect(() => {
    setTypedText("");
    setTypingComplete(false);

    let index = 0;
    const text = fullText;

    const typingInterval = setInterval(() => {
      if (index < text.length) {
        setTypedText(text.substring(0, index + 1));
        index++;
      } else {
        clearInterval(typingInterval);
        setTypingComplete(true);
      }
    }, 40);

    return () => clearInterval(typingInterval);
  }, [fullText, language]);

  // Memoize navigation links to avoid re-renders
  const navLinks = useMemo(
    () => [
      { id: "about", label: translations[language].about },
      { id: "skills", label: translations[language].skills },
      { id: "experience", label: translations[language].experience },
      { id: "projects", label: translations[language].projects },
    ],
    [language]
  );

  return (
    <>
      <header className="lg:sticky lg:top-0 lg:flex lg:max-h-screen lg:w-[48%] lg:flex-col lg:justify-between lg:py-24">
        {/* Mobile Banner - Only visible on mobile */}
        <div className="lg:hidden relative w-full h-32 mb-4 overflow-hidden rounded-lg">
          <img
            src="/images/banner.gif"
            alt="Banner"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
            className="w-full h-full object-cover" // banner-green
          />
          {/* Green color overlay to shift GIF hues towards green */}
          {/* <div className="absolute inset-0 banner-overlay-green" aria-hidden="true"></div> */}
          {/* <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div> */}
        </div>

        <div className="relative">
          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center mb-12 hud-section gap-4">
            {/* <div className="lg:w-[35%]">
              <div className="relative w-32 h-32">
                <img
                  src="/images/profile.jpeg"
                  alt="Desy Adelina R"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  className="w-32 h-32 rounded-full object-cover holographic-image"
                />
                <div className="absolute inset-0 rounded-full border-2 border-cyan-400/30 animate-pulse"></div>
                <div className="absolute -inset-1 rounded-full border border-cyan-400/20"></div>
                <div className="absolute -inset-2 rounded-full border border-cyan-400/10"></div>
              </div>
            </div> */}
            <div className="w-full">
              <h1 className="text-4xl font-bold tracking-tight text-cyan-50 font-mono">
                <a href="/" className="relative inline-block group">
                  <span className="relative z-10">Desy Adelina R</span>
                  <span className="absolute left-0 -bottom-1 w-0 h-0.5 bg-cyan-400 transition-all duration-300 group-hover:w-full"></span>
                </a>
              </h1>
              <h2 className="mt-3 text-lg font-medium tracking-tight text-cyan-200 sm:text-xl font-mono">
                <span className="text-cyan-400">function</span>{" "}
                <span className="text-white">{t.role}</span>()
              </h2>
              <div className="mt-3 max-w-xs leading-normal font-mono bg-cyan-900/10 p-3 border-l-2 border-cyan-400/60 relative">
                
                <p className="text-cyan-100">
                  {typedText}
                  {!typingComplete && (
                    <span className="inline-block w-2 h-4 bg-cyan-400 ml-1 animate-blink"></span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Mobile Layout - LinkedIn style */}
          <div className="lg:hidden">
            {/* Profile Section */}
            <div className="flex flex-col items-center text-center mb-6 -mt-16 relative z-10">
              <div className="relative w-24 h-24 mb-4">
                <img
                  src="/images/profile.jpeg"
                  alt="Desy Adelina R"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  className="w-24 h-24 rounded-full object-cover holographic-image border-1 border-cyan-200"
                />
                <div className="absolute inset-0 rounded-full border-2 border-cyan-400/30 animate-pulse"></div>
              </div>

              <h1 className="text-2xl font-bold tracking-tight text-cyan-50 font-mono">
                <a href="/" className="relative inline-block group">
                  <span className="relative z-10">Desy Adelina R</span>
                  <span className="absolute left-0 -bottom-1 w-0 h-0.5 bg-cyan-400 transition-all duration-300 group-hover:w-full"></span>
                </a>
              </h1>

              <h2 className="mt-2 text-base font-medium tracking-tight text-cyan-200 font-mono">
                <span className="text-cyan-400">function</span>{" "}
                <span className="text-white">{t.role}</span>()
              </h2>

              <div className="mt-3 w-full max-w-sm leading-normal font-mono bg-cyan-900/10 p-3 border-l-2 border-cyan-400/60 text-left">
                <p className="text-cyan-100 text-[12px]">
                  {typedText}
                  {!typingComplete && (
                    <span className="inline-block w-2 h-4 bg-cyan-400 ml-1 animate-blink"></span>
                  )}
                </p>
              </div>

              {/* Mobile Social Links */}
              <div className="mt-4">
                <div className="text-xs text-cyan-400 mb-2 font-mono opacity-70 text-center">
                  CONNECT.PROTOCOLS
                </div>
                <ul
                  className="flex items-center justify-center gap-4"
                  aria-label="Social media"
                >
                  <li className="text-xs shrink-0">
                    <a
                      className="block hover:text-cyan-400 transition-colors duration-300 glow-effect"
                      href="https://github.com/desyadelina"
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label="GitHub (opens in a new tab)"
                      title="GitHub"
                    >
                      <span className="sr-only">GitHub</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 16 16"
                        fill="currentColor"
                        className="h-6 w-6"
                        aria-hidden="true"
                      >
                        <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"></path>
                      </svg>
                    </a>
                  </li>
                  <li className="text-xs shrink-0">
                    <a
                      className="block hover:text-cyan-400 transition-colors duration-300 glow-effect"
                      href="https://www.linkedin.com/in/desyadelina/"
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label="LinkedIn (opens in a new tab)"
                      title="LinkedIn"
                    >
                      <span className="sr-only">LinkedIn</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="h-6 w-6"
                        aria-hidden="true"
                      >
                        <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
                      </svg>
                    </a>
                  </li>
                  <li className="text-xs shrink-0">
                    <a
                      className="block hover:text-cyan-400 transition-colors duration-300 glow-effect"
                      href="https://instagram.com/desyadeline"
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label="Instagram (opens in a new tab)"
                      title="Instagram"
                    >
                      <span className="sr-only">Instagram</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 1000 1000"
                        fill="currentColor"
                        className="h-6 w-6"
                        aria-hidden="true"
                      >
                        <path d="M295.42,6c-53.2,2.51-89.53,11-121.29,23.48-32.87,12.81-60.73,30-88.45,57.82S40.89,143,28.17,175.92c-12.31,31.83-20.65,68.19-23,121.42S2.3,367.68,2.56,503.46,3.42,656.26,6,709.6c2.54,53.19,11,89.51,23.48,121.28,12.83,32.87,30,60.72,57.83,88.45S143,964.09,176,976.83c31.8,12.29,68.17,20.67,121.39,23s70.35,2.87,206.09,2.61,152.83-.86,206.16-3.39S799.1,988,830.88,975.58c32.87-12.86,60.74-30,88.45-57.84S964.1,862,976.81,829.06c12.32-31.8,20.69-68.17,23-121.35,2.33-53.37,2.88-70.41,2.62-206.17s-.87-152.78-3.4-206.1-11-89.53-23.47-121.32c-12.85-32.87-30-60.7-57.82-88.45S862,40.87,829.07,28.19c-31.82-12.31-68.17-20.7-121.39-23S637.33,2.3,501.54,2.56,348.75,3.4,295.42,6m5.84,903.88c-48.75-2.12-75.22-10.22-92.86-17-23.36-9-40-19.88-57.58-37.29s-28.38-34.11-37.5-57.42c-6.85-17.64-15.1-44.08-17.38-92.83-2.48-52.69-3-68.51-3.29-202s.22-149.29,2.53-202c2.08-48.71,10.23-75.21,17-92.84,9-23.39,19.84-40,37.29-57.57s34.1-28.39,57.43-37.51c17.62-6.88,44.06-15.06,92.79-17.38,52.73-2.5,68.53-3,202-3.29s149.31.21,202.06,2.53c48.71,2.12,75.22,10.19,92.83,17,23.37,9,40,19.81,57.57,37.29s28.4,34.07,37.52,57.45c6.89,17.57,15.07,44,17.37,92.76,2.51,52.73,3.08,68.54,3.32,202s-.23,149.31-2.54,202c-2.13,48.75-10.21,75.23-17,92.89-9,23.35-19.85,40-37.31,57.56s-34.09,28.38-57.43,37.5c-17.6,6.87-44.07,15.07-92.76,17.39-52.73,2.48-68.53,3-202.05,3.29s-149.27-.25-202-2.53m407.6-674.61a60,60,0,1,0,59.88-60.1,60,60,0,0,0-59.88,60.1M245.77,503c.28,141.8,115.44,256.49,257.21,256.22S759.52,643.8,759.25,502,643.79,245.48,502,245.76,245.5,361.22,245.77,503m90.06-.18a166.67,166.67,0,1,1,167,166.34,166.65,166.65,0,0,1-167-166.34"></path>
                      </svg>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <nav className="nav hidden lg:block" aria-label="In-page jump links">
            <div className="text-xs text-cyan-400 mb-2 font-mono opacity-70">
              SYSTEM.NAVIGATION
            </div>
            <ul className="w-max">
              {navLinks.map((section) => (
                <li key={section.id}>
                  <a
                    className={`group flex items-center py-3 transition-all duration-300 ${
                      activeSection === section.id
                        ? "text-cyan-400"
                        : "text-white"
                    }`}
                    href={`#${section.id}`}
                  >
                    <span
                      className={`nav-indicator mr-4 h-px ${
                        activeSection === section.id
                          ? "bg-cyan-400 w-16"
                          : "bg-slate-600 w-8"
                      } transition-all duration-300 group-hover:w-16 group-hover:bg-cyan-400 group-focus-visible:w-16 group-focus-visible:bg-cyan-400 motion-reduce:transition-none`}
                    ></span>
                    <span className="nav-text text-xs font-bold uppercase tracking-widest group-hover:text-cyan-400 group-focus-visible:text-cyan-400 font-mono">
                      {section.label}
                    </span>

                    {/* active indicator dot */}
                    {activeSection === section.id && (
                      <span className="ml-2 w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></span>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Desktop Social Links */}
        <div className="ml-1 mt-8 hidden lg:block">
          <div className="text-xs text-cyan-400 mb-2 font-mono opacity-70">
            CONNECT.PROTOCOLS
          </div>
          <ul className="flex items-center" aria-label="Social media">
            <li className="mr-5 text-xs shrink-0">
              <a
                className="block hover:text-cyan-400 transition-colors duration-300 glow-effect"
                href="https://github.com/desyadelina"
                target="_blank"
                rel="noreferrer noopener"
                aria-label="GitHub (opens in a new tab)"
                title="GitHub"
              >
                <span className="sr-only">GitHub</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  className="h-6 w-6"
                  aria-hidden="true"
                >
                  <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"></path>
                </svg>
              </a>
            </li>
            <li className="mr-5 text-xs shrink-0">
              <a
                className="block hover:text-cyan-400 transition-colors duration-300 glow-effect"
                href="https://www.linkedin.com/in/desyadelina/"
                target="_blank"
                rel="noreferrer noopener"
                aria-label="LinkedIn (opens in a new tab)"
                title="LinkedIn"
              >
                <span className="sr-only">LinkedIn</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="h-6 w-6"
                  aria-hidden="true"
                >
                  <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
                </svg>
              </a>
            </li>
            <li className="mr-5 text-xs shrink-0">
              <a
                className="block hover:text-cyan-400 transition-colors duration-300 glow-effect"
                href="https://instagram.com/desyadeline"
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Instagram (opens in a new tab)"
                title="Instagram"
              >
                <span className="sr-only">Instagram</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 1000 1000"
                  fill="currentColor"
                  className="h-6 w-6"
                  aria-hidden="true"
                >
                  <path d="M295.42,6c-53.2,2.51-89.53,11-121.29,23.48-32.87,12.81-60.73,30-88.45,57.82S40.89,143,28.17,175.92c-12.31,31.83-20.65,68.19-23,121.42S2.3,367.68,2.56,503.46,3.42,656.26,6,709.6c2.54,53.19,11,89.51,23.48,121.28,12.83,32.87,30,60.72,57.83,88.45S143,964.09,176,976.83c31.8,12.29,68.17,20.67,121.39,23s70.35,2.87,206.09,2.61,152.83-.86,206.16-3.39S799.1,988,830.88,975.58c32.87-12.86,60.74-30,88.45-57.84S964.1,862,976.81,829.06c12.32-31.8,20.69-68.17,23-121.35,2.33-53.37,2.88-70.41,2.62-206.17s-.87-152.78-3.4-206.1-11-89.53-23.47-121.32c-12.85-32.87-30-60.7-57.82-88.45S862,40.87,829.07,28.19c-31.82-12.31-68.17-20.7-121.39-23S637.33,2.3,501.54,2.56,348.75,3.4,295.42,6m5.84,903.88c-48.75-2.12-75.22-10.22-92.86-17-23.36-9-40-19.88-57.58-37.29s-28.38-34.11-37.5-57.42c-6.85-17.64-15.1-44.08-17.38-92.83-2.48-52.69-3-68.51-3.29-202s.22-149.29,2.53-202c2.08-48.71,10.23-75.21,17-92.84,9-23.39,19.84-40,37.29-57.57s34.1-28.39,57.43-37.51c17.62-6.88,44.06-15.06,92.79-17.38,52.73-2.5,68.53-3,202-3.29s149.31.21,202.06,2.53c48.71,2.12,75.22,10.19,92.83,17,23.37,9,40,19.81,57.57,37.29s28.4,34.07,37.52,57.45c6.89,17.57,15.07,44,17.37,92.76,2.51,52.73,3.08,68.54,3.32,202s-.23,149.31-2.54,202c-2.13,48.75-10.21,75.23-17,92.89-9,23.35-19.85,40-37.31,57.56s-34.09,28.38-57.43,37.5c-17.6,6.87-44.07,15.07-92.76,17.39-52.73,2.48-68.53,3-202.05,3.29s-149.27-.25-202-2.53m407.6-674.61a60,60,0,1,0,59.88-60.1,60,60,0,0,0-59.88,60.1M245.77,503c.28,141.8,115.44,256.49,257.21,256.22S759.52,643.8,759.25,502,643.79,245.48,502,245.76,245.5,361.22,245.77,503m90.06-.18a166.67,166.67,0,1,1,167,166.34,166.65,166.65,0,0,1-167-166.34"></path>
                </svg>
              </a>
            </li>
          </ul>
        </div>
      </header>
    </>
  );
}

export default React.memo(Hero);